// Forwarding for the path prefixes the SPAs used to be mounted under.
//
// Each audience now owns a hostname, so a prefix like `/ops` or `/admin` only
// repeated what the host already said. The prefixes cannot simply be deleted:
// invite and password-reset emails ALREADY SENT point at prefixed paths, and an
// email cannot be recalled — so a legacy URL is forwarded to its short form.
//
// Shared by the ops and admin SPAs rather than copied into each: the rule is
// identical and only the prefix differs, and two copies of a path rewriter is
// two places for a boundary bug to hide.
//
// NOTE the admin SPA only mounts its forward in `audiences` mode. In `legacy`
// mode its router basename IS `/admin`, so the prefix is consumed before any
// route matches — and on a shared wildcard host that prefix is what Cloudflare
// uses to choose the admin Worker over the portal one, so forwarding it away
// would send the browser to a path another Worker serves.

/**
 * Drop a leading path prefix, or return the input unchanged.
 *
 * Only an exact match or a `/`-delimited boundary counts, so `/opsimposter` is
 * not treated as `/ops` + `imposter`. A bare `startsWith(prefix)` would rewrite
 * it to `/imposter` and silently send the user somewhere else.
 */
export function stripPathPrefix(prefix: string, pathname: string): string {
  if (pathname === prefix || pathname === `${prefix}/`) return '/';
  if (pathname.startsWith(`${prefix}/`)) return pathname.slice(prefix.length);
  return pathname;
}

/**
 * The full location to forward a legacy request to, query and hash intact.
 *
 * Preserving `search` is load-bearing rather than tidy: accept-invite and
 * reset-password links carry their one-time token there, so dropping it would
 * turn a working invite into a dead one — the exact failure this forward exists
 * to prevent.
 */
export function legacyPathTarget(
  prefix: string,
  pathname: string,
  search: string,
  hash: string,
): string {
  return `${stripPathPrefix(prefix, pathname)}${search}${hash}`;
}
